import type { PlayerRef } from '@remotion/player';
import { Internals } from 'remotion';
import { useContext, useEffect, useRef, useState } from 'react';

const MIN_DB = -60;
const MAX_DB = 0;

function timeDomainToDbFs(data: Uint8Array): number {
  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    const normalized = (data[i]! - 128) / 128;
    sum += normalized * normalized;
  }
  const rms = Math.sqrt(sum / data.length);
  if (rms <= 1e-10) return MIN_DB;
  const db = 20 * Math.log10(rms);
  return Math.max(MIN_DB, Math.min(MAX_DB, db));
}

// ---------------------------------------------------------------------------
// Remotion Player 안에서 SharedAudioContext의 master gain을 직접 구독합니다.
// Timeline은 Player Provider 바깥에 있으므로 bridge가 analyser를 등록하고,
// usePlayerOutputAudioDb가 동일한 module state를 읽습니다.
// ---------------------------------------------------------------------------

interface MeterState {
  analyser: AnalyserNode;
  ctx: AudioContext;
  gainNode: GainNode;
}

let meterState: MeterState | null = null;

export function PlayerOutputAudioMeterBridge() {
  const sharedAudioContext = useContext(Internals.SharedAudioContext);
  const audioContext = sharedAudioContext?.audioContext ?? null;
  const gainNode = sharedAudioContext?.gainNode ?? null;

  useEffect(() => {
    if (!audioContext || !gainNode) {
      return;
    }

    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.72;
    gainNode.connect(analyser);

    const nextState = { analyser, ctx: audioContext, gainNode };
    meterState = nextState;

    return () => {
      gainNode.disconnect(analyser);
      analyser.disconnect();
      if (meterState === nextState) {
        meterState = null;
      }
    };
  }, [audioContext, gainNode]);

  return null;
}

/**
 * Remotion Player의 최종 오디오 출력 레벨(dBFS)을 실시간 반환합니다.
 *
 * Remotion은 OffthreadVideo/Html5Audio + useWebAudioApi를 사용하며,
 * 내부 SharedAudioContext(sampleRate=48000)를 통해 오디오를 재생합니다.
 */
export function usePlayerOutputAudioDb(
  playerRef: React.RefObject<PlayerRef | null>,
): number {
  const [dbFs, setDbFs] = useState(MIN_DB);
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    let cancelled = false;

    const tick = () => {
      if (cancelled) return;

      const p = playerRef.current;
      const playing = p?.isPlaying?.() ?? false;
      const muted = p?.isMuted?.() ?? false;

      if (
        meterState &&
        playing &&
        !muted &&
        meterState.ctx.state === 'running'
      ) {
        const buf = new Uint8Array(meterState.analyser.frequencyBinCount);
        meterState.analyser.getByteTimeDomainData(buf);
        setDbFs(timeDomainToDbFs(buf));
      } else {
        setDbFs(MIN_DB);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, [playerRef]);

  return dbFs;
}
